import { LocalizationInputGroup, type LocalizationValue } from '@/backoffice/components/forms/LocalizationInputGroup';
import type { FormState, RegisterReference } from '@/shared/types/users.types';
import type { C } from '@/utils/colorHelpers';
import { availabilitySlotLabel } from '@/shared/utils/registerReferenceLabels';
import { useState } from 'react';
import { Button } from '@/backoffice/components/ui';
import { Plus, X } from 'lucide-react';
import { resolveSecteur } from '@/domains/api/geography.api';

export function StepAvailability({
    form,
    toggleSlot,
    addCoverageSecteur,
    removeCoverageSecteur,
    reference,
    c,
}: {
    form: FormState;
    toggleSlot: (id: number) => void;
    addCoverageSecteur: (id: number, label: string) => void;
    removeCoverageSecteur: (id: number) => void;
    reference: RegisterReference;
    c: C;
}) {
    const [loc, setLoc] = useState<LocalizationValue>({
        countryId: null,
        regionId: null,
        prefectureId: null,
        communeId: null,
        districtId: null,
        secteurId: null,
    });
    const [locError, setLocError] = useState<string | null>(null);

    const handleAddSecteur = async () => {
        if (!loc.secteurId) {
            setLocError('Sélectionnez un secteur complet.');
            return;
        }
        if (form.coverageSecteurIds.includes(loc.secteurId)) {
            setLocError('Ce secteur est déjà ajouté.');
            return;
        }
        setLocError(null);
        try {
            const hierarchy = await resolveSecteur(loc.secteurId);
            const label = [
                hierarchy.secteur.name,
                hierarchy.district?.name,
                hierarchy.commune?.name,
            ].filter(Boolean).join(' · ');
            addCoverageSecteur(loc.secteurId, label);
            setLoc({
                countryId: null,
                regionId: null,
                prefectureId: null,
                communeId: null,
                districtId: null,
                secteurId: null,
            });
        } catch {
            setLocError('Impossible de valider ce secteur.');
        }
    };

    return (
        <div className="space-y-5">
            <h2 className="text-xl font-semibold text-gray-900">Disponibilité et zones</h2>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Créneaux *</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {reference.availability_slots.map(slot => (
                        <label
                            key={slot.id}
                            className={`flex items-center gap-2 text-sm cursor-pointer p-3 border rounded-lg transition-all ${
                                form.availabilitySlotIds.includes(slot.id)
                                    ? `${c.border} ${c.light}`
                                    : 'border-gray-200 hover:bg-gray-50'
                            }`}
                        >
                            <input
                                type="checkbox"
                                checked={form.availabilitySlotIds.includes(slot.id)}
                                onChange={() => toggleSlot(slot.id)}
                                className="rounded border-gray-300"
                            />
                            <span>
                                {availabilitySlotLabel(slot.name)}
                                {slot.start_time && slot.end_time
                                    ? ` (${slot.start_time.slice(0, 5)}–${slot.end_time.slice(0, 5)})`
                                    : ''}
                            </span>
                        </label>
                    ))}
                </div>
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Secteurs de couverture *</label>
                <p className="text-sm text-gray-500 mb-3">
                    Indiquez les secteurs où vous pouvez collecter ou livrer des colis.
                </p>

                {form.coverageSecteurLabels.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3">
                        {form.coverageSecteurLabels.map((entry) => (
                            <span
                                key={entry.id}
                                className={`inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded-full border ${c.border} ${c.light}`}
                            >
                                {entry.label}
                                <button
                                    type="button"
                                    onClick={() => removeCoverageSecteur(entry.id)}
                                    className="p-0.5 rounded-full hover:bg-white/60"
                                >
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            </span>
                        ))}
                    </div>
                )}

                <LocalizationInputGroup value={loc} onChange={setLoc} className="mb-3" />
                {locError && <p className="text-sm text-red-600 mb-2">{locError}</p>}
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    leftIcon={<Plus className="w-4 h-4" />}
                    onClick={handleAddSecteur}
                    disabled={!loc.secteurId}
                >
                    Ajouter ce secteur
                </Button>
            </div>
        </div>
    );
}
